import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faRefresh,
  faCopy,
  faBan,
  faEdit,
  faHistory,
  faCheck,
  faTimes,
  faExclamationTriangle,
  faKey,
} from '@fortawesome/free-solid-svg-icons';
import '../styles/ApiKeyManagement.css';

const BASE_URL = process.env.REACT_APP_SERVER_BASE_URL;
const API_PATH = '/hospital-portal/api/external-api-management';

const ApiKeyManagement = () => {
  const { user, rolePermissions } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    partner_name: '',
    environment: 'development',
    allowed_endpoints: ['/api/medical/v1/samples'],
    allowed_ips: '',
    rate_limit_per_minute: 60,
    expires_at: '',
  });

  // Generated key display
  const [generatedKey, setGeneratedKey] = useState(null);
  const [keyCopied, setKeyCopied] = useState(false);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState(null);

  // Logs modal state
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsKeyName, setLogsKeyName] = useState('');
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);

  const canAdd = rolePermissions['/api-key-management']?.Add === 'true';
  const canEdit = rolePermissions['/api-key-management']?.Edit === 'true';
  const canDelete = rolePermissions['/api-key-management']?.Delete === 'true';

  const fetchKeys = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${BASE_URL}${API_PATH}/api-keys`);
      setKeys(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      const payload = {
        ...createForm,
        allowed_ips: createForm.allowed_ips
          ? createForm.allowed_ips.split(',').map(ip => ip.trim()).filter(Boolean)
          : null,
      };

      const response = await axios.post(`${BASE_URL}${API_PATH}/api-keys`, payload);
      setGeneratedKey(response.data.data.plainKey);
      setKeyCopied(false);
      setShowCreateModal(false);
      fetchKeys();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create API key');
    }
  };

  const handleCopyKey = async () => {
    if (generatedKey) {
      await navigator.clipboard.writeText(generatedKey);
      setKeyCopied(true);
    }
  };

  const handleRevoke = async (id, name) => {
    if (!window.confirm(`Are you sure you want to revoke the API key "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${BASE_URL}${API_PATH}/api-keys/${id}`);
      fetchKeys();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to revoke API key');
    }
  };

  const handleEdit = (key) => {
    setEditForm({
      id: key.id,
      name: key.name,
      partner_name: key.partner_name,
      allowed_endpoints: key.allowed_endpoints || ['/api/medical/v1/samples'],
      allowed_ips: key.allowed_ips ? key.allowed_ips.join(', ') : '',
      rate_limit_per_minute: key.rate_limit_per_minute,
      expires_at: key.expires_at ? key.expires_at.split('T')[0] : '',
    });
    setShowEditModal(true);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      const payload = {
        ...editForm,
        allowed_ips: editForm.allowed_ips
          ? editForm.allowed_ips.split(',').map(ip => ip.trim()).filter(Boolean)
          : null,
      };
      delete payload.id;

      await axios.put(`${BASE_URL}${API_PATH}/api-keys/${editForm.id}`, payload);
      setShowEditModal(false);
      setEditForm(null);
      fetchKeys();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update API key');
    }
  };

  const handleViewLogs = async (keyId, keyName) => {
    try {
      setLogsKeyName(keyName);
      setLogsPage(1);
      const response = await axios.get(`${BASE_URL}${API_PATH}/api-keys/${keyId}/logs`, {
        params: { page: 1, limit: 20 },
      });
      setLogs(response.data.data.logs || []);
      setLogsTotal(response.data.data.total || 0);
      setShowLogsModal(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load logs');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="api-key-management">
      <div className="api-key-header">
        <h2><FontAwesomeIcon icon={faKey} /> External API Key Management</h2>
        <div className="api-key-actions">
          <button className="btn-refresh" onClick={fetchKeys} disabled={loading}>
            <FontAwesomeIcon icon={faRefresh} spin={loading} /> Refresh
          </button>
          {canAdd && (
            <button className="btn-create" onClick={() => setShowCreateModal(true)}>
              <FontAwesomeIcon icon={faPlus} /> Generate New Key
            </button>
          )}
        </div>
      </div>

      {error && <div className="api-key-error"><FontAwesomeIcon icon={faExclamationTriangle} /> {error}</div>}

      {/* Generated Key Display - shown once after creation */}
      {generatedKey && (
        <div className="api-key-generated">
          <div className="generated-warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <strong> This API key will only be shown once.</strong> Copy it now and store it securely.
          </div>
          <div className="generated-key-row">
            <code className="generated-key-value">{generatedKey}</code>
            <button className="btn-copy" onClick={handleCopyKey}>
              <FontAwesomeIcon icon={keyCopied ? faCheck : faCopy} />
              {keyCopied ? ' Copied!' : ' Copy'}
            </button>
          </div>
          <button className="btn-dismiss" onClick={() => setGeneratedKey(null)}>Dismiss</button>
        </div>
      )}

      {/* Keys Table */}
      <div className="api-key-table-wrapper">
        <table className="api-key-table">
          <thead>
            <tr>
              <th>Key Prefix</th>
              <th>Name</th>
              <th>Partner</th>
              <th>Environment</th>
              <th>Status</th>
              <th>Requests</th>
              <th>Last Used</th>
              <th>Expires</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {keys.length === 0 && !loading && (
              <tr><td colSpan="10" className="empty-row">No API keys found</td></tr>
            )}
            {keys.map((key) => (
              <tr key={key.id} className={!key.is_active ? 'row-revoked' : ''}>
                <td><code>{key.key_prefix}...</code></td>
                <td>{key.name}</td>
                <td>{key.partner_name}</td>
                <td>
                  <span className={`env-badge env-${key.environment}`}>
                    {key.environment}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${key.is_active ? 'status-active' : 'status-revoked'}`}>
                    {key.is_active ? 'Active' : 'Revoked'}
                  </span>
                </td>
                <td>{key.requestCount || 0}</td>
                <td>{formatDate(key.last_used_at)}</td>
                <td>{key.expires_at ? formatDate(key.expires_at) : 'Never'}</td>
                <td>{formatDate(key.createdAt)}</td>
                <td className="actions-cell">
                  <button className="btn-icon" title="View Logs" onClick={() => handleViewLogs(key.id, key.name)}>
                    <FontAwesomeIcon icon={faHistory} />
                  </button>
                  {canEdit && key.is_active && (
                    <button className="btn-icon" title="Edit" onClick={() => handleEdit(key)}>
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                  )}
                  {canDelete && key.is_active && (
                    <button className="btn-icon btn-icon-danger" title="Revoke" onClick={() => handleRevoke(key.id, key.name)}>
                      <FontAwesomeIcon icon={faBan} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Generate New API Key</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g., Sysmex CBC Analyzer - Prod"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Partner Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g., Sysmex"
                  value={createForm.partner_name}
                  onChange={(e) => setCreateForm({ ...createForm, partner_name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Environment *</label>
                <select
                  value={createForm.environment}
                  onChange={(e) => setCreateForm({ ...createForm, environment: e.target.value })}
                >
                  <option value="development">Development</option>
                  <option value="production">Production</option>
                </select>
              </div>
              <div className="form-group">
                <label>Rate Limit (requests/minute)</label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={createForm.rate_limit_per_minute}
                  onChange={(e) => setCreateForm({ ...createForm, rate_limit_per_minute: parseInt(e.target.value) || 60 })}
                />
              </div>
              <div className="form-group">
                <label>IP Whitelist (comma-separated, leave empty for any)</label>
                <input
                  type="text"
                  placeholder="e.g., 192.168.1.100, 10.0.0.50"
                  value={createForm.allowed_ips}
                  onChange={(e) => setCreateForm({ ...createForm, allowed_ips: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Expiry Date (optional)</label>
                <input
                  type="date"
                  value={createForm.expires_at}
                  onChange={(e) => setCreateForm({ ...createForm, expires_at: e.target.value })}
                />
              </div>
              <div className="modal-buttons">
                <button type="button" className="btn-cancel" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="btn-create">Generate Key</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editForm && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit API Key</h3>
            <form onSubmit={handleEditSave}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  required
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Partner Name</label>
                <input
                  type="text"
                  required
                  value={editForm.partner_name}
                  onChange={(e) => setEditForm({ ...editForm, partner_name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Rate Limit (requests/minute)</label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={editForm.rate_limit_per_minute}
                  onChange={(e) => setEditForm({ ...editForm, rate_limit_per_minute: parseInt(e.target.value) || 60 })}
                />
              </div>
              <div className="form-group">
                <label>IP Whitelist (comma-separated)</label>
                <input
                  type="text"
                  value={editForm.allowed_ips}
                  onChange={(e) => setEditForm({ ...editForm, allowed_ips: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Expiry Date</label>
                <input
                  type="date"
                  value={editForm.expires_at}
                  onChange={(e) => setEditForm({ ...editForm, expires_at: e.target.value })}
                />
              </div>
              <div className="modal-buttons">
                <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="submit" className="btn-create">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Logs Modal */}
      {showLogsModal && (
        <div className="modal-overlay" onClick={() => setShowLogsModal(false)}>
          <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3>API Logs — {logsKeyName}</h3>
            <div className="logs-summary">Total requests: {logsTotal}</div>
            <table className="api-key-table logs-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Method</th>
                  <th>Endpoint</th>
                  <th>Status</th>
                  <th>IP</th>
                  <th>Time (ms)</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && (
                  <tr><td colSpan="7" className="empty-row">No logs found</td></tr>
                )}
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td>{formatDate(log.createdAt)}</td>
                    <td><span className={`method-badge method-${log.method}`}>{log.method}</span></td>
                    <td className="endpoint-cell">{log.endpoint}</td>
                    <td>
                      <span className={`status-code ${log.status_code >= 400 ? 'status-error' : 'status-ok'}`}>
                        {log.status_code}
                      </span>
                    </td>
                    <td>{log.ip_address}</td>
                    <td>{log.response_time_ms}</td>
                    <td className="error-cell">{log.error_message || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="modal-buttons">
              <button type="button" className="btn-cancel" onClick={() => setShowLogsModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeyManagement;
